import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MdLockOutline, MdOutlineWifiOff } from 'react-icons/md';
import Button from '~/components/base/Button';
import Spinner from '~/components/base/Spinner';
import Typography from '~/components/base/Typography';
import AdminMfaModal from '~/components/modules/AdminMfaModal';
import useAdminAccess from '~/hooks/useAdminAccess';
import useAdminMfaGate from '~/hooks/useAdminMfaGate';
import { setAdminMfaRequired } from '~/redux/modules/adminMfa';
import { useAppDispatch } from '~/redux/hooks';
import './admin-mfa-gate.scss';

type Props = {
    children: JSX.Element
}

/**
 * Holds every authenticated screen shut until the member has verified.
 *
 * Wraps the render inside PrivateRoute, which is the one place every
 * `authedRoute: true` route passes through. That placement is the whole design:
 * the Core web app gates per screen with a HOC, and two screens had silently
 * missed it — a route added tomorrow is covered here without anyone
 * remembering to do anything.
 *
 * Renders exactly one of four states:
 *
 * | State                       | Content            | Modal |
 * | --------------------------- | ------------------ | ----- |
 * | unknown / checking          | centred spinner    | no    |
 * | required                    | nothing            | yes   |
 * | required, modal dismissed   | centred Verify me  | no    |
 * | not eligible                | refusal, no code   | no    |
 * | valid                       | the screen         | no    |
 *
 * Three properties are load-bearing:
 *
 * **Three states, not two.** On a reload the status passes through 'unknown'
 * and 'checking' before it resolves, and treating "we don't know yet" as "no
 * verification needed" renders a frame of real case data before the modal
 * lands on top of it.
 *
 * **The screen is unmounted, never hidden.** A mounted component still runs
 * its effects and fires its requests — here that means pulling member records
 * and welfare cases for a session that may be about to be refused. `display:
 * none` would not do.
 *
 * **The modal stays mounted while dismissed.** useAdminMfaChallenge requests a
 * code on mount, so if dismissing unmounted it, Verify me would issue a fresh
 * challenge on every press and a few presses would burn the resend ladder.
 * Hidden and unmounted are deliberately different: hidden keeps the
 * outstanding challenge and both its countdowns alive, so reopening lands on
 * the code already in the inbox. A member lock is the one exception — it
 * unmounts, because nobody asked for a code by locking.
 *
 * @param {Object} props children the screen to gate
 * @returns {JSX.Element} the screen, or what stands in for it
 */
export default function AdminMfaGate({ children }: Props): JSX.Element {
    const { isMfaDue, isResolving, isCheckFailed, isIneligible, isMemberLocked } = useAdminMfaGate();
    // Callback only. The app-wide instance in App.tsx owns the check on mount;
    // a second auto-firing copy here would call the endpoint again on every
    // authenticated screen.
    const { onCheckAccess } = useAdminAccess({ isAutoCheck: false });
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();

    // Seeded from the lock: a member who closed Admin themselves gets the
    // panel, not the code prompt. Everything else opens the prompt as before.
    const [isModalClosed, setIsModalClosed] = useState(isMemberLocked);

    // A dismissal applies to the screen it happened on. Navigating is a fresh
    // request to work, so the ask comes back rather than leaving them on a
    // placeholder they have to know to click — unless they locked Admin, which
    // is a decision that travels with them.
    useEffect(() => {
        setIsModalClosed(isMemberLocked);
    }, [isMemberLocked, pathname]);

    const onCloseModal = useCallback(() => setIsModalClosed(true), []);

    // Verify me is the member changing their mind, so the lock is lifted here
    // rather than left to re-close the modal on the next render.
    const onReopenModal = useCallback(() => {
        if (isMemberLocked) dispatch(setAdminMfaRequired());

        setIsModalClosed(false);
    }, [dispatch, isMemberLocked]);

    if (isResolving) {
        return (
            <div className='admin-mfa-gate'>
                <Spinner isCentered={true} />
            </div>
        );
    }

    // Authenticated but reported ineligible. See useAdminMfaGate for why this
    // refuses rather than passing through or asking for a code.
    if (isIneligible) {
        return (
            <div className='admin-mfa-gate'>
                <div className='admin-mfa-gate__locked'>
                    <MdLockOutline className='admin-mfa-gate__icon' />
                    <Typography size={16} weight='semi-bold' color='neutral-500'>
                        No access
                    </Typography>
                    <Typography size={14} color='davy-grey-500'>
                        This account has no welfare permissions. Ask a super admin to grant them.
                    </Typography>
                </div>
            </div>
        );
    }

    // The check did not come back. Not a refusal — offer the question again
    // rather than a code prompt that would go over the same failed connection.
    // onCheckAccess sets 'checking' itself, so the spinner above covers the
    // retry and this panel cannot be pressed twice into two in-flight requests.
    if (isCheckFailed) {
        return (
            <div className='admin-mfa-gate'>
                <div className='admin-mfa-gate__locked'>
                    <MdOutlineWifiOff className='admin-mfa-gate__icon' />
                    <Typography size={16} weight='semi-bold' color='neutral-500'>
                        Can&apos;t check your session
                    </Typography>
                    <Typography size={14} color='davy-grey-500'>
                        We couldn&apos;t reach the server. Check your connection and try again.
                    </Typography>
                    <Button label='Try again' size='lg' onClick={onCheckAccess} />
                </div>
            </div>
        );
    }

    if (isMfaDue) {
        return (
            <div className='admin-mfa-gate'>
                {isModalClosed && (
                    <div className='admin-mfa-gate__locked'>
                        <MdLockOutline className='admin-mfa-gate__icon' />
                        <Typography size={16} weight='semi-bold' color='neutral-500'>
                            Locked
                        </Typography>
                        <Typography size={14} color='davy-grey-500'>
                            Verify your email to carry on.
                        </Typography>
                        <Button label='Verify me' size='lg' onClick={onReopenModal} />
                    </div>
                )}
                {/*
                    Kept mounted while merely dismissed, so the outstanding
                    challenge and its countdowns survive — that is the whole
                    reason this is hidden rather than unmounted.

                    A member lock is the other way to be hidden, and there it
                    must unmount: useAdminMfaChallenge requests a code on mount,
                    and locking sets the status to 'required', so staying mounted
                    means Lock now silently emails a code and starts its ten
                    minutes running. By the time they press Verify me that code
                    can already be spent, which disables Verify and leaves resend
                    the only way forward.
                */}
                {!isMemberLocked && (
                    <AdminMfaModal isShow={!isModalClosed} onClose={onCloseModal} />
                )}
            </div>
        );
    }

    return children;
}
